import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { getCleanThreadId } from '../views/AppNavigator/Messages/helpers'
import toast from 'react-hot-toast'
import { ArchiveThreadResponse } from './types'

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

export interface ArchiveThreadParams {
  thread_id: string
}

/**
 * React Query hook for archiving a thread
 * Uses the thread read endpoint with archive=true parameter
 */
export const useArchiveThreadMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation<ArchiveThreadResponse, Error, ArchiveThreadParams>({
    mutationFn: async (params: ArchiveThreadParams) => {
      try {
        const { thread_id } = params
        const cleanThreadId = getCleanThreadId(thread_id)

        // Use the read endpoint with archive=true query parameter
        const response = await fetchWithAuth(
          `${URL}/threads/${cleanThreadId}/read?archive=true`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )

        return response as ArchiveThreadResponse
      } catch (error) {
        console.error('Error archiving thread:', error)
        toast.error('Unable to archive thread. Please try again later.')
        throw error
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['threads'] })
      toast.success('Thread archived successfully')
    },
  })
}
